import { RecordMark } from "@rosc/ui/v32/record-button";
import { Waveform } from "@rosc/ui/v32/recorder-bar";
import { t } from "i18next";
import { CircleCheck, MicOff } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export type MicTestState = "testing" | "working" | "error";

// The first-run microphone check: asks for the microphone and shows its
// level in the v3.2 recorder's waveform pill (live bars, dotted rest), then
// whether it works.
export const OnboardingMicTest = ({
  preview,
}: {
  // Stories and screenshots: a fixed state (and levels), no microphone.
  preview?: { state: MicTestState; levels?: number[] };
}): JSX.Element => {
  const [live, setLive] = useState<MicTestState>("testing");
  const [stream, setStream] = useState<MediaStream | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const state = preview?.state ?? live;

  useEffect(() => {
    if (preview) return;
    let cancelled = false;
    void navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((next) => {
        if (cancelled) {
          next.getTracks().forEach((track) => {
            track.stop();
          });
          return;
        }
        streamRef.current = next;
        setStream(next);
        setLive("working");
      })
      .catch(() => {
        if (!cancelled) setLive("error");
      });

    return (): void => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => {
        track.stop();
      });
    };
  }, [preview]);

  return (
    <div className="space-y-3">
      <div
        className="flex h-[56px] items-center gap-3 rounded-full bg-v3-panel py-2 pl-2 pr-4 shadow-v3-float ring-1 ring-v3-border"
        data-state={state}
        data-testid="onboarding-mic-meter"
      >
        <RecordMark
          icon={
            state === "error" ? <MicOff className="size-[18px]" /> : undefined
          }
          size="meter"
          tone={state === "working" ? "soft" : "muted"}
        />
        <Waveform
          className={cn(
            "min-w-0 flex-1",
            state === "working" ? "text-v3-text" : "text-v3-text-3",
            state === "testing" && "animate-pulse",
          )}
          label={t("recorderV32.micLevel")}
          paused={state !== "working"}
          levels={preview?.levels ?? (state === "working" ? undefined : [])}
          stream={state === "working" ? stream : null}
        />
      </div>

      {state === "working" && (
        <div className="flex min-h-11 items-center gap-2 rounded-xl bg-v3-ok-tint px-3.5 py-2">
          <CircleCheck className="size-4 shrink-0 text-v3-ok-text" />
          <span className="text-[13.5px] font-semibold text-v3-ok-text">
            {t("onboarding.form.micWorking")}
          </span>
        </div>
      )}

      {state === "error" && (
        <div
          className="flex min-h-11 items-center gap-2 rounded-xl bg-v3-rec-tint px-3.5 py-2"
          role="alert"
        >
          <span className="text-[13.5px] font-semibold text-v3-rec-text">
            {t("onboarding.form.micError")}
          </span>
        </div>
      )}
    </div>
  );
};
